import React, { useMemo } from 'react';

import Timeline from 'react-calendar-timeline';

import { TimelineScale } from '../../types/interfaces';

import TimelineHeader from './TimelineHeader';
import {
  CalendarAssignmentsDesktopLayout,
  CalendarAssignmentsTimelineActions,
  CalendarAssignmentsTimelineModel
} from './types';

export interface CalendarAssignmentsTimelineProps {
  model: CalendarAssignmentsTimelineModel;
  actions: CalendarAssignmentsTimelineActions;
  layout: CalendarAssignmentsDesktopLayout;
  employeesTimelineRef: React.RefObject<HTMLDivElement>;
  employeesTimelinePointerHandlers?: React.HTMLAttributes<HTMLDivElement>;
  scrollBarOffset: number;
  scrollHeight: string;
  onEmployeesScroll: () => void;
}

function getMinZoom(scale: TimelineScale, monthDurationMs: number) {
  if (scale === TimelineScale.DAY) return 3600 * 1000;
  if (scale === TimelineScale.WEEK) return 86400 * 1000;
  return monthDurationMs;
}

export function CalendarAssignmentsTimeline({
  model,
  actions,
  layout,
  employeesTimelineRef,
  employeesTimelinePointerHandlers,
  scrollBarOffset,
  scrollHeight,
  onEmployeesScroll
}: CalendarAssignmentsTimelineProps) {
  const departmentGroups = useMemo(
    () =>
      model.groups.filter(
        (group) => group.id === layout.departmentScheduleGroupId
      ),
    [model.groups, layout.departmentScheduleGroupId]
  );
  const employeeGroups = useMemo(
    () =>
      model.groups.filter(
        (group) => group.id !== layout.departmentScheduleGroupId
      ),
    [model.groups, layout.departmentScheduleGroupId]
  );
  const departmentItems = useMemo(
    () =>
      model.items.filter(
        (item) => item.group === layout.departmentScheduleGroupId
      ),
    [model.items, layout.departmentScheduleGroupId]
  );
  const employeeItems = useMemo(
    () =>
      model.items.filter(
        (item) => item.group !== layout.departmentScheduleGroupId
      ),
    [model.items, layout.departmentScheduleGroupId]
  );
  const minZoom = getMinZoom(model.timelineScale, model.monthDurationMs);
  const layoutVariables = useMemo(
    () =>
      ({
        '--calendar-assignments-content-min-width': `${layout.contentMinWidth}px`,
        '--calendar-assignments-timeline-min-width': `${layout.timelineMinWidth}px`,
        '--calendar-assignments-scrollbar-offset': `${scrollBarOffset}px`,
        '--calendar-assignments-scroll-height': scrollHeight
      }) as React.CSSProperties,
    [
      layout.contentMinWidth,
      layout.timelineMinWidth,
      scrollBarOffset,
      scrollHeight
    ]
  );

  const commonTimelineProps = useMemo(
    () => ({
      itemHeightRatio: 1,
      visibleTimeStart: model.visibleTimeStart,
      visibleTimeEnd: model.visibleTimeEnd,
      minZoom,
      maxZoom: model.monthDurationMs,
      canMove: false,
      canResize: false,
      canChangeGroup: false,
      sidebarWidth: model.sidebarWidth,
      onItemSelect: actions.onItemSelect,
      onItemClick: actions.onItemSelect,
      onItemDeselect: actions.onItemDeselect,
      onItemDoubleClick: actions.onItemDoubleClick,
      onTimeChange: actions.onTimeChange,
      selected: model.selectedItemIds
    }),
    [
      model.visibleTimeStart,
      model.visibleTimeEnd,
      model.monthDurationMs,
      model.sidebarWidth,
      model.selectedItemIds,
      minZoom,
      actions.onItemSelect,
      actions.onItemDeselect,
      actions.onItemDoubleClick,
      actions.onTimeChange
    ]
  );

  return (
    <div
      className="timeline-head calendarAssignments--content"
      style={layoutVariables}
    >
      <div className="calendarAssignments--timeline-header-inner">
        <TimelineHeader
          startOfMonth={model.startOfMonth}
          daysInMonth={model.daysInMonth}
          sidebarWidth={model.sidebarWidth}
          filters={model.filters}
          sortBy={model.sortBy}
          sortDirection={model.sortDirection}
          onFilterChange={actions.onFilterChange}
          onSortChange={actions.onSortChange}
          timelineScale={model.timelineScale}
          onScaleChange={actions.onScaleChange}
          baseDate={model.visibleTimeRange}
          showOnlyFreeEmployees={model.showOnlyFreeEmployees}
          onToggleFreeEmployeesFilter={actions.onToggleFreeEmployeesFilter}
          excludeLongAbsences={model.excludeLongAbsences}
          onToggleExcludeLongAbsences={actions.onToggleExcludeLongAbsences}
          todayShiftOnly={model.todayShiftOnly}
          onToggleTodayShiftOnly={actions.onToggleTodayShiftOnly}
          key={model.timelineScale}
        />
      </div>

      <div className="timeline-wrapper calendarAssignments--timeline-wrapper">
        <div className="calendarAssignments--timeline-inner">
          <div className="calendarAssignments--department-timeline">
            <Timeline
              {...commonTimelineProps}
              buffer={1}
              lineHeight={layout.lineHeight}
              key={`department-${model.timelineVersion}`}
              groups={departmentGroups}
              items={departmentItems}
            />
          </div>

          <div
            ref={employeesTimelineRef}
            className="hidden-scroll calendarAssignments--employees-timeline"
            onScroll={onEmployeesScroll}
            {...employeesTimelinePointerHandlers}
          >
            <Timeline
              {...commonTimelineProps}
              lineHeight={layout.lineHeight}
              key={`employees-${model.timelineVersion}`}
              groups={employeeGroups}
              items={employeeItems}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
